import { useQuery } from '@tanstack/react-query'
import { api } from './api.js'

export interface Me {
  id: string
  email: string
  role: string
  academyId?: string
  /** True for a brand-new self-signup with no academy and no linked children. */
  needsOnboarding?: boolean
  /** Academy lifecycle status — drives the suspended/cancelled lockout screen. */
  academyStatus?: string
}

/**
 * Imperative /me fetch for use outside React (e.g. router `beforeLoad`).
 * Returns null when unauthenticated or on error.
 */
export async function fetchMe(): Promise<Me | null> {
  const res = await api<Me>('/api/v1/me')
  return 'data' in res ? res.data : null
}

/**
 * The signed-in user's identity + app role + academy, from GET /api/v1/me.
 *
 * Better Auth uses opaque session tokens (not a JWT with embedded claims), so
 * the SPA can't decode role/academy from the token like it did with Supabase.
 * This is the single client source for role-based nav + feature gating.
 */
export function useMe(enabled = true): { me: Me | null; isLoading: boolean } {
  const query = useQuery({
    queryKey: ['me'],
    enabled,
    // Short window so an academy suspension/reactivation surfaces in the SPA
    // promptly (the lockout in _app.tsx keys off me.academyStatus). The API is
    // the authoritative gate; this just keeps the UI in step. Also refetch on
    // focus so a backgrounded tab catches a status change on return.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Me | null> => {
      const res = await api<Me>('/api/v1/me')
      return 'data' in res ? res.data : null
    },
  })
  return { me: query.data ?? null, isLoading: enabled && query.isLoading }
}
